import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { User, LoginCredentials, RegisterData } from '../types/auth';
import { authService } from '../services/auth';
import { logComponentVersion } from '../utils/version';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Log component initialization
  useEffect(() => {
    logComponentVersion('🔐 AuthContext', 'Initializing simplified authentication provider');
  }, []);

  // Create user profile from session data (no API calls to avoid deadlock)
  const createUserFromSession = useCallback((supabaseUser: SupabaseUser): User => {
    // console.log('🔐 AuthContext: Creating user from session data for:', supabaseUser.email);
    return {
      id: supabaseUser.id,
      email: supabaseUser.email || '',
      firstName: supabaseUser.user_metadata?.first_name || '',
      lastName: supabaseUser.user_metadata?.last_name || '',
      isVerified: supabaseUser.email_confirmed_at !== null,
      isAdmin: false, // Will be enhanced later if needed
      createdAt: supabaseUser.created_at,
      updatedAt: supabaseUser.updated_at || supabaseUser.created_at,
    };
  }, []);

  // Enhanced profile loading (called outside auth callback to avoid deadlock)
  const enhanceUserProfile = useCallback(async (userId: string): Promise<void> => {
    try {
      // console.log('🔐 AuthContext: Enhancing user profile for:', userId);
      
      // Get profile from database (safe to call outside auth callback)
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profile && !error) {
        // console.log('🔐 AuthContext: Profile enhancement successful');
        setUser(prevUser => {
          if (!prevUser) return prevUser;
          return {
            ...prevUser,
            firstName: profile.first_name || prevUser.firstName,
            lastName: profile.last_name || prevUser.lastName,
            isAdmin: profile.is_admin || false,
          };
        });
      } else {
        // console.log('🔐 AuthContext: No profile found or error:', error?.message);
      }
    } catch (error) {
      console.warn('🔐 AuthContext: Profile enhancement failed:', error);
      // Non-critical error, user can still use the app
    }
  }, []);

  // Initialize auth state - OFFICIAL SUPABASE PATTERN
  useEffect(() => {
    // console.log('🔐 AuthContext: Initializing auth state...');
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // console.log('🔐 AuthContext: Initial session check:', session ? 'Found' : 'None');
      setSession(session);
      
      if (session?.user) {
        // Create user from session data immediately (no API calls)
        const userProfile = createUserFromSession(session.user);
        setUser(userProfile);
        // console.log('🔐 AuthContext: Initial user profile created from session');
        
        // Schedule profile enhancement outside the auth callback (Supabase recommended pattern)
        setTimeout(() => {
          enhanceUserProfile(session.user.id);
        }, 0);
      }
      
      setIsLoading(false);
      // console.log('🔐 AuthContext: Initial auth check complete');
    });

    // Listen for auth changes - OFFICIAL SUPABASE PATTERN
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // console.log('🔐 AuthContext: Auth state change:', event);
      setSession(session);
      
      if (event === 'SIGNED_IN' && session?.user) {
        // console.log('🔐 AuthContext: User signed in, creating profile from session...');
        // Create user from session data immediately (no API calls in callback)
        const userProfile = createUserFromSession(session.user);
        setUser(userProfile);
        // console.log('🔐 AuthContext: Profile created from session on sign in');
        
        // Schedule profile enhancement outside the auth callback (Supabase recommended pattern)
        setTimeout(() => {
          enhanceUserProfile(session.user.id);
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        // console.log('🔐 AuthContext: User signed out');
        setUser(null);
      }
      
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [createUserFromSession, enhanceUserProfile]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      setError(null);

      // console.log('🔐 AuthContext: Starting login for', credentials.email);

      const { error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        console.error('🔐 AuthContext: Login failed:', error.message);
        setError(error.message);
        throw new Error(error.message);
      }

      // console.log('🔐 AuthContext: Login successful, auth state change will handle profile loading');
      // Don't set user data here - the auth state change listener will handle it
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      console.error('🔐 AuthContext: Login error:', errorMessage);
      setError(errorMessage);
      setIsLoading(false);
      throw error;
    }
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.register(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // console.log('🔐 AuthContext: Logging out...');
      await supabase.auth.signOut();
      // Auth state change listener will handle clearing user data
    } catch (error) {
      console.error('🔐 AuthContext: Logout error:', error);
      // Force clear state even if logout fails
      setSession(null);
      setUser(null);
    }
  }, []);


  const forgotPassword = useCallback(async (email: string) => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.forgotPassword(email);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.resetPassword(token, password);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      setError(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    session,
    user,
    isLoading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper hook for checking authentication status
export const useAuthStatus = () => {
  const { session, user, isLoading } = useAuth();
  
  return {
    isAuthenticated: !!session && !!user,
    isLoading,
    session,
    user,
  };
};
